/**
 * Bottom Sheet View (Portrait/Mobile)
 *
 * A mobile-style bottom sheet that slides up from the bottom with:
 * - Slide-up animation on open
 * - Drag-to-dismiss gesture support
 * - Drag handle indicator
 * - Scroll prevention when not at top
 * - Smart dragging (only when content at top)
 */

import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { SheetSurfaceProvider } from '@/presentation/contexts/SheetSurfaceContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useScrollPrevention } from '../hooks/useScrollPrevention';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { SHEET_CONSTANTS } from '@/presentation/constants/sheetConstants';
import { CTAButtons } from './CTAButtons';
import { qaAttr } from '@/presentation/utils/qaAttr';
import type { SheetState } from '../config/ExpauseSheet.types';

interface BottomSheetViewProps {
  state: SheetState;
  onClose: () => void;
  zIndex: number;
  canClose: boolean;
}

export const BottomSheetView: React.FC<BottomSheetViewProps> = ({
  state,
  onClose,
  zIndex,
  canClose,
}) => {
  const { theme } = useTheme();
  const responsiveDesign = useResponsiveDesign();
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const buttonsRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const preventClickRef = useRef(false);
  const dragOffsetRef = useRef(0); // Track current drag offset for mouse handlers

  // Whether swipe-to-dismiss is enabled (default: true)
  const dismissible = state.dismissible ?? true;

  // Animation and drag state
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [dragState, setDragState] = useState({
    isDragging: false,
    startY: 0,
    dragOffset: 0,
  });

  // Trigger slide-up animation on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      setIsAnimatingIn(true);
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Unified function to animate sheet down and close
  // currentOffset: optional parameter to specify where the sheet currently is (for drag-to-dismiss)
  const animateAndClose = (currentOffset: number = 0) => {
    if (!sheetRef.current) {
      onClose();
      return;
    }

    // Set animating out state to prevent JSX from controlling transform
    setIsAnimatingOut(true);

    // Calculate remaining distance and proportional duration
    const sheetHeight = sheetRef.current.offsetHeight;
    const remainingDistance = sheetHeight - currentOffset;
    const distanceRatio = remainingDistance / sheetHeight;

    // Base duration scaled by distance ratio with minimum
    const duration = Math.max(
      ANIMATION_CONSTANTS.duration.minDragDismiss,
      Math.round(ANIMATION_CONSTANTS.duration.normal * distanceRatio)
    );

    // Use requestAnimationFrame to ensure state update has been processed
    requestAnimationFrame(() => {
      if (!sheetRef.current) return;

      // If we have a current offset (dragged position), first set the transform without transition
      // This ensures we start the animation from the correct position
      if (currentOffset > 0) {
        sheetRef.current.style.transition = 'none';
        sheetRef.current.style.transform = `translateY(${currentOffset}px)`;

        // Force a reflow to ensure the transform is applied before we start the transition
        void sheetRef.current.offsetHeight;
      }

      // Now animate down from current position
      sheetRef.current.style.transition = `transform ${duration}ms ${ANIMATION_CONSTANTS.easing.easeOut}`;
      sheetRef.current.style.transform = 'translateY(100%)';
    });

    // Call onClose after animation
    setTimeout(() => {
      onClose();
    }, duration);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (canClose && e.target === e.currentTarget) {
      animateAndClose();
    }
  };

  // Resolve the element that actually owns the scroll under this gesture.
  // With `scrollable: false` the shell content div is `overflow: hidden` and its
  // scrollTop is permanently 0, so consulting it would always green-light a drag
  // even while a nested body-owned scroller is mid-list. For `scrollable !== false`
  // sheets the shell is the scroller, so behaviour is unchanged.
  const resolveScroller = (target: EventTarget | null): HTMLElement | null => {
    const content = contentRef.current;
    if (state.scrollable !== false) return content;

    let el = target instanceof HTMLElement ? target : null;
    while (el && el !== content) {
      const overflowY = getComputedStyle(el).overflowY;
      const scrolls = overflowY === 'auto' || overflowY === 'scroll';
      if (scrolls && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return content;
  };

  // Handle drag start (touch or mouse)
  // checkScrollPosition: if true, only allow drag if content is at top (for content area)
  // if false, always allow drag (for title, handle, buttons areas)
  // target: the event target the gesture started on, used to find the nested
  // scroller when the shell has stood down as a scroller (`scrollable: false`)
  const handleDragStart = (
    clientY: number,
    checkScrollPosition: boolean = true,
    target: EventTarget | null = null
  ) => {
    if (!canClose || !dismissible) return false;

    if (checkScrollPosition) {
      const scroller = resolveScroller(target);

      // Check if the owning scroller is scrollable and not at top
      const isScrollable = scroller && scroller.scrollHeight > scroller.clientHeight;
      const isAtTop = !scroller || scroller.scrollTop <= 0;

      // Only allow dragging if that scroller is at top (or not scrollable)
      if (!(!isScrollable || isAtTop)) {
        return false;
      }
    }

    // Reset ref
    dragOffsetRef.current = 0;

    setDragState({
      isDragging: true,
      startY: clientY,
      dragOffset: 0,
    });
    return true;
  };

  // Handle drag move (touch or mouse)
  const handleDragMove = (clientY: number, preventDefault: () => void) => {
    if (!dragState.isDragging) return;

    const deltaY = clientY - dragState.startY;

    // Only allow dragging down (positive deltaY)
    if (deltaY > 0) {
      preventDefault(); // Prevent scrolling/selection while dragging

      // If dragged more than a few pixels, prevent click events
      if (deltaY > 5) {
        preventClickRef.current = true;
      }

      // Update ref for mouse handlers
      dragOffsetRef.current = deltaY;

      setDragState(prev => ({
        ...prev,
        dragOffset: deltaY,
      }));
    }
  };

  // Handle drag end (touch or mouse)
  const handleDragEnd = () => {
    if (!dragState.isDragging) return;

    // Use ref value which is always up to date (needed for mouse handlers)
    const dragOffset = dragOffsetRef.current;

    // Check if should dismiss based on drag distance
    const shouldDismiss = dragOffset > SHEET_CONSTANTS.dismissThreshold;

    if (shouldDismiss && canClose) {
      // Reset drag state first so animation can take over
      setDragState(prev => ({
        ...prev,
        isDragging: false,
      }));
      // Animate out and close from current drag position
      animateAndClose(dragOffset);
    } else {
      // Snap back to original position
      setDragState(prev => ({
        ...prev,
        isDragging: false,
        dragOffset: 0,
      }));
    }

    // Reset ref
    dragOffsetRef.current = 0;

    // Reset click prevention after a short delay to allow drag to complete
    setTimeout(() => {
      preventClickRef.current = false;
    }, 100);
  };

  // Touch event handlers - for non-content areas (always allow drag)
  const handleTouchStartNonContent = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientY, false);
  };

  // Touch event handlers - for content area (check scroll position)
  const handleTouchStartContent = (e: React.TouchEvent) => {
    // Don't interfere with input elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    const touch = e.touches[0];
    const started = handleDragStart(touch.clientY, true, target);
    // Only prevent default if we actually started dragging
    // This allows normal scrolling when content is not at top
    if (started) {
      e.preventDefault();
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientY, () => e.preventDefault());
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  // Mouse event handlers - for non-content areas (always allow drag)
  const handleMouseDownNonContent = (e: React.MouseEvent) => {
    const started = handleDragStart(e.clientY, false);
    if (started) {
      e.preventDefault(); // Prevent text selection
    }
  };

  // Mouse event handlers - for content area (check scroll position)
  const handleMouseDownContent = (e: React.MouseEvent) => {
    // Don't interfere with input elements
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
      return;
    }

    const started = handleDragStart(e.clientY, true, target);
    if (started) {
      e.preventDefault(); // Prevent text selection
    }
  };

  // Prevent clicks after drag
  const handleClickCapture = (e: React.MouseEvent) => {
    if (preventClickRef.current) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  // Global mouse event listeners for drag (must be global to handle drag outside sheet)
  useEffect(() => {
    if (!dragState.isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientY, () => e.preventDefault());
    };

    const handleMouseUp = () => {
      handleDragEnd();
    };

    // Add global listeners
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState.isDragging, dragState.startY]);

  // Prevent scroll propagation to body (wheel events only, touch is handled by drag handlers)
  // When scrollable is false, skip content ref so nested scroll containers can work
  const scrollPreventionContentRef = state.scrollable === false
    ? { current: null } as React.RefObject<HTMLDivElement>
    : contentRef;
  useScrollPrevention(scrollPreventionContentRef, [overlayRef, titleRef, buttonsRef], { preventTouch: false });

  const content = (
    <>
      {/* Overlay */}
      <div
        ref={overlayRef}
        {...qaAttr({ id: 'sheet-backdrop' })}
        className={`${state.container ? 'absolute' : 'fixed'} inset-0`}
        style={{
          backgroundColor: theme.modalOverlay,
          zIndex,
          pointerEvents: 'auto',
          userSelect: 'none',
          opacity: isAnimatingIn ? 1 : 0,
          transition: dragState.isDragging ? 'none' : `opacity ${ANIMATION_CONSTANTS.duration.slow}ms ease-out`,
        }}
        onClick={handleOverlayClick}
        data-sheet-overlay={state.id}
        data-has-container={!!state.container}
      />

      {/* Bottom Sheet Content */}
      <div
        ref={sheetRef}
        {...qaAttr({ id: state.qaId ? `sheet-${state.qaId}` : 'sheet-bottom', status: 'open' })}
        className={`flex flex-col ${state.container ? 'absolute' : 'fixed'} outline-none`}
        style={{
          backgroundColor: theme.modalBackground,
          borderTopLeftRadius: responsiveDesign.bottomSheet.radius,
          borderTopRightRadius: responsiveDesign.bottomSheet.radius,
          borderBottomLeftRadius: '0',
          borderBottomRightRadius: '0',
          width: '100%',
          ...(state.fixedHeight
            ? { height: state.fixedHeight, maxHeight: state.fixedHeight }
            : { maxHeight: state.maxHeight || responsiveDesign.bottomSheet.maxHeight }),
          left: 0,
          bottom: 0,
          paddingBottom: responsiveDesign.safeArea.bottom, // iOS safe area support
          zIndex: zIndex + 1,
          pointerEvents: 'auto',
          userSelect: dragState.isDragging ? 'none' : 'text',
          // Don't control transform when animating out - let imperative animation take over
          ...(!isAnimatingOut && {
            transform: dragState.isDragging
              ? `translateY(${dragState.dragOffset}px)`
              : isAnimatingIn
                ? 'translateY(0)'
                : 'translateY(100%)',
            transition: dragState.isDragging
              ? 'none'
              : `transform ${ANIMATION_CONSTANTS.duration.slow}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
          }),
        }}
        onTouchMove={dismissible ? handleTouchMove : undefined}
        onTouchEnd={dismissible ? handleTouchEnd : undefined}
        onClickCapture={dismissible ? handleClickCapture : undefined}
        data-sheet-content={state.id}
        data-sheet-title={state.title}
        data-has-container={!!state.container}
      >
        {/* Top Bar with Drag Handle and/or Title */}
        {(state.dragIndicator !== false || state.topBarTitle) && (
          <div
            {...qaAttr({ id: 'sheet-close' })}
            className="flex justify-center items-center flex-shrink-0 relative"
            style={{
              paddingTop: responsiveDesign.spacing.base,
              paddingBottom: responsiveDesign.spacing.sm,
              touchAction: dismissible ? 'none' : 'auto',
              userSelect: 'none',
            }}
            onTouchStart={dismissible ? handleTouchStartNonContent : undefined}
            onMouseDown={dismissible ? handleMouseDownNonContent : undefined}
          >
            {/* Drag Indicator */}
            {state.dragIndicator !== false && (
              <div
                style={{
                  width: responsiveDesign.bottomSheet.topBar.dragIndicator.width,
                  height: responsiveDesign.bottomSheet.topBar.dragIndicator.height,
                  borderRadius: responsiveDesign.bottomSheet.topBar.dragIndicator.borderRadius,
                  backgroundColor: theme.text.secondary || theme.border,
                  opacity: 0.4,
                }}
              />
            )}

            {/* Top Bar Title (centered, absolute positioned) */}
            {state.topBarTitle && (
              <div
                className="absolute"
                style={{
                  fontSize: responsiveDesign.font.sm,
                  color: theme.text.primary,
                  fontWeight: responsiveDesign.fontWeight.medium,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                {state.topBarTitle}
              </div>
            )}
          </div>
        )}

        {/* Title */}
        {state.title && (
          <div
            ref={titleRef}
            className="font-semibold border-b flex-shrink-0"
            style={{
              paddingLeft: responsiveDesign.spacing['2xl'],
              paddingRight: responsiveDesign.spacing['2xl'],
              paddingTop: responsiveDesign.spacing.base,
              paddingBottom: responsiveDesign.spacing.base,
              fontSize: responsiveDesign.font.lg,
              color: theme.text.primary,
              borderBottomColor: theme.transparent, // Clear border for aesthetics
              touchAction: dismissible ? 'none' : 'auto',
              userSelect: 'none',
            }}
            onTouchStart={dismissible ? handleTouchStartNonContent : undefined}
            onMouseDown={dismissible ? handleMouseDownNonContent : undefined}
          >
            {state.title}
          </div>
        )}

        {/* Content */}
        <div
          ref={contentRef}
          className={`flex-1 ${state.scrollable !== false ? 'overflow-y-auto scrollbar-hide' : ''}`}
          style={{
            color: theme.text.primary,
            userSelect: 'text',
            overscrollBehavior: 'contain', // Prevent overscroll from affecting parent
            minHeight: 0, // Allow flex item to shrink for nested scroll to work
            // When scrollable is false, use overflow:hidden to constrain content
            // so nested scroll containers can work properly
            ...(state.scrollable === false && {
              overflow: 'hidden',
            }),
            // Disable scrolling when sheet is being dragged down
            ...(dismissible && dragState.dragOffset > 0 && {
              overflow: 'hidden',
              touchAction: 'none',
            }),
          }}
          onTouchStart={dismissible ? handleTouchStartContent : undefined}
          onMouseDown={dismissible ? handleMouseDownContent : undefined}
        >
          <SheetSurfaceProvider>{state.content}</SheetSurfaceProvider>
        </div>

        {/* CTA Buttons */}
        {state.cta && (
          <div
            ref={buttonsRef}
            className="flex-shrink-0"
            style={{
              touchAction: dismissible ? 'none' : 'auto',
              userSelect: 'none',
            }}
            onTouchStart={dismissible ? handleTouchStartNonContent : undefined}
            onMouseDown={dismissible ? handleMouseDownNonContent : undefined}
          >
            <CTAButtons cta={state.cta} onClose={animateAndClose} />
          </div>
        )}
      </div>
    </>
  );

  // Render into container or body
  if (state.container) {
    return createPortal(content, state.container);
  }
  return createPortal(content, document.body);
};
